import {LINKER_PAYLOAD_LEAVING_GROUP_REQUIRES_LINKER_PAYLOAD_ERROR} from 'molbio/adcs/linker-payload-connection/AdcLinkerPayloadLeavingGroupConstants';
import {parseLeavingGroupTypedValue} from 'molbio/adcs/linker-payload-connection/AdcLinkerPayloadLeavingGroupSerialization';
import {invalidLinkerPayloadLeavingGroupError} from 'molbio/adcs/linker-payload-connection/AdcLinkerPayloadLeavingGroupUtils';
import {LinkerPayloadLeavingGroupsResult} from 'molbio/adcs/linker-payload-connection/linkerPayloadLeavingGroupsQuery';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {getBioentityIdFromTypedValue} from 'notebook/tables/search-scoping/rowScopedSearch';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';

/**
 * Custom eval for the ADC Linker-payload leaving group column, which checks the selected leaving group against
 * the leaving groups on the row's linker-payload molecule.
 *
 * cellDependencies layout (from RegistrationStructuredTableAdapter.getTableSpecificCustomEvalArgs):
 *   [0] = ADC_LINKER_PAYLOAD (molecule BIOENTITY)
 *
 * The column is optional, so an empty cell has no error, including when the linker-payload has no leaving groups.
 * A linker-payload that is filled in but invalid reports its own error, so the leaving group is not checked
 * against it. The cell's value is always preserved.
 */
export function adcLinkerPayloadLeavingGroupColumnCustomEval(
  typedValue: TypedValue,
  _columnDefinition: ColumnDefinition | null,
  cellDependencies: Array<TypedValue | null>,
  _customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  const result = (error: CustomEvalResult['error']): CustomEvalResult => ({
    val: typedValue,
    error,
    locked: false,
  });

  const parsed = parseLeavingGroupTypedValue(typedValue);
  if ('error' in parsed) {
    return result(new ExcessUtil.ValueError(parsed.error));
  }
  const {leavingGroup} = parsed;
  if (leavingGroup == null) {
    return result(null);
  }

  const linkerPayloadValue = cellDependencies[0] ?? null;
  if (linkerPayloadValue == null || isTypedValueEmpty(linkerPayloadValue)) {
    return result(new ExcessUtil.ValueError(LINKER_PAYLOAD_LEAVING_GROUP_REQUIRES_LINKER_PAYLOAD_ERROR));
  }
  const moleculeId = getBioentityIdFromTypedValue(linkerPayloadValue);
  if (moleculeId == null) {
    return result(null);
  }

  const {resolvedValue, error: resolutionError} = resolveFromResolutionQuery(
    typedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType:
        CustomResolutionQueryType.REGISTRATION_TABLE_ADC_LINKER_PAYLOAD_LEAVING_GROUPS,
      moleculeId,
    },
    makeTypedValueFromCustomResolvedValue
  );
  if (resolutionError != null) {
    return result(resolutionError);
  }
  if (resolvedValue?.type !== BaseTypedValueType.JSON) {
    return result(null);
  }

  const leavingGroupsResult = resolvedValue.jsonValue as LinkerPayloadLeavingGroupsResult;
  if ('error' in leavingGroupsResult) {
    return result(new ExcessUtil.ValueError(leavingGroupsResult.error));
  }
  if (!leavingGroupsResult.leavingGroups.includes(leavingGroup)) {
    return result(new ExcessUtil.ValueError(invalidLinkerPayloadLeavingGroupError(leavingGroup)));
  }
  return result(null);
}
